import { Platform } from 'react-native';
import PostHog from 'posthog-react-native';

import {
  getBuildBranch,
  getPostHogApiKey,
  getPostHogEnabled,
  getPostHogHost,
  getVidaEnv,
  isEmbedded,
} from '../../config/runtimeConfig';

// RN polyfills a global `window`, so `typeof window !== 'undefined'` isn't
// a valid web check. Use Platform.OS.
const isWeb = Platform.OS === 'web';

export let posthog: PostHog | null = null;
let configured = false;

// Adopt a host-owned client so login's telemetry lands on the host's
// identified session. Blocks the standalone init path below.
//
// Guarded on client identity so a re-render of LoginRoot (which calls this
// each pass) doesn't clobber in-flight state; only an actual client swap
// (logout/login, re-identify) resets the load-tracking bookkeeping and
// re-subscribes to the new client's onFeatureFlags stream.
//
// Seeds _hostFlagsSettled synchronously from the client's current state.
// onFeatureFlags is a plain event emitter with no last-event replay, so a
// host that resolved flags before LoginRoot mounted (the common case — host
// inits PostHog at app boot, login mounts on a user gesture much later)
// would otherwise leave _hostFlagsSettled `false` forever and every variant
// fall-through would burn the full HOST_SETTLE_TIMEOUT_MS.
export const setHostPostHogClient = (client: PostHog): void => {
  if (client === posthog) return;
  posthog = client;
  configured = true;
  _hostFlagsSettled = Boolean(client.getFeatureFlags());
  _hostSettleWaiters = [];
  _flagsLoaded = false;
  _reloadPromise = null;
  _hostFlagsUnsubscribe?.();
  _hostFlagsUnsubscribe = client.onFeatureFlags(() => {
    _hostFlagsSettled = true;
    const waiters = _hostSettleWaiters;
    _hostSettleWaiters = [];
    for (const resolve of waiters) resolve();
  });
};

// Standalone-only fallback; no-op if setHostPostHogClient already ran.
//
// getPostHogEnabled/getPostHogApiKey/getPostHogHost throw when required env
// vars aren't resolved yet — the caller may be an embed host whose `config`
// prop is still momentarily undefined on first render (falls into this
// branch under the LoginRoot gate below), or a standalone SPA whose
// EXPO_PUBLIC_POSTHOG_* env vars didn't inline. In either case, silently
// no-op and leave `configured = false` so a subsequent render can retry
// once the settled state arrives.
export const configureLoginPostHog = (): void => {
  if (configured) return;

  let apiKey: string;
  let host: string;
  let enabled: boolean;
  try {
    enabled = getPostHogEnabled();
    if (!enabled) {
      configured = true;
      return;
    }
    apiKey = getPostHogApiKey();
    host = getPostHogHost();
  } catch {
    return;
  }

  configured = true;
  posthog = new PostHog(apiKey, {
    host,
    // No cookies / localStorage — pre-auth page, anonymous session per load.
    persistence: 'memory',
    preloadFeatureFlags: true,
  });

  const release = getBuildBranch();
  posthog.register({
    service: 'login',
    environment: getVidaEnv(),
    platform: Platform.OS,
    ...(release ? { release } : {}),
  });
};

export const posthogTrackEvent = (name: string, properties?: Record<string, any>): void => {
  try {
    posthog?.capture(name, properties);
  } catch (e) {
    console.error('[PostHog] trackEvent failed', e);
  }
};

const FLUSH_TIMEOUT_MS = 1000;

// The SDK batches and its requests don't use keepalive, so anything still
// queued when the page navigates away is lost.
export const posthogFlush = async (): Promise<void> => {
  if (!posthog) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      posthog.flush(),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, FLUSH_TIMEOUT_MS);
      }),
    ]);
  } catch (e) {
    console.error('[PostHog] flush failed', e);
  } finally {
    clearTimeout(timer);
  }
};

export const posthogTrackScreen = (name: string, properties?: Record<string, any>): void => {
  try {
    posthog?.capture('$pageview', {
      $current_url: isWeb ? window.location.href : name,
      $pathname: name,
      ...properties,
    });
  } catch (e) {
    console.error('[PostHog] trackScreen failed', e);
  }
};

export const posthogTrackError = (error: Error | string, context?: Record<string, any>): void => {
  try {
    const message = error instanceof Error ? error.message : error;
    posthog?.capture('$exception', { message, ...context });
  } catch (e) {
    console.error('[PostHog] trackError failed', e);
  }
};

// Standalone: dedupes concurrent reloadFeatureFlagsAsync calls — only one
// in-flight at a time. _flagsLoaded prevents re-fetching for unknown keys
// once flags have been loaded. Written for login's own `persistence: 'memory'`
// client where every page load starts cold.
//
// Embed: firing reloadFeatureFlagsAsync on the host's shared singleton would
// trigger the host's onFeatureFlags listeners app-wide. Instead we passively
// subscribe (in setHostPostHogClient) and await the host's own first
// onFeatureFlags event so a variant fall-through (`variantFlags[key]: undefined`)
// can resolve after cold host mount rather than freezing on the initial
// `getFeatureFlag(key) === undefined` read.
let _reloadPromise: Promise<void> | null = null;
let _flagsLoaded = false;
let _hostFlagsUnsubscribe: (() => void) | undefined;
let _hostFlagsSettled = false;
let _hostSettleWaiters: Array<() => void> = [];
const HOST_SETTLE_TIMEOUT_MS = 2000;

const ensureFlagsLoaded = (): Promise<void> => {
  if (!posthog) return Promise.resolve();
  if (isEmbedded()) {
    if (_hostFlagsSettled) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        // Latch settled=true on either path (onFeatureFlags OR the timeout)
        // so subsequent lookups short-circuit rather than restarting the
        // 2s wait for a signal that isn't going to come.
        _hostFlagsSettled = true;
        resolve();
      };
      _hostSettleWaiters.push(finish);
      setTimeout(finish, HOST_SETTLE_TIMEOUT_MS);
    });
  }
  if (_flagsLoaded) return Promise.resolve();
  if (!_reloadPromise) {
    _reloadPromise = posthog
      .reloadFeatureFlagsAsync()
      .then(() => {
        _flagsLoaded = true;
      })
      .finally(() => {
        _reloadPromise = null;
      });
  }
  return _reloadPromise;
};

export const posthogGetFeatureFlag = async (key: string): Promise<string | boolean | undefined> => {
  try {
    if (!posthog) return undefined;
    const cached = posthog.getFeatureFlag(key);
    if (cached !== undefined) return cached;
    await ensureFlagsLoaded();
    return posthog.getFeatureFlag(key);
  } catch (e) {
    console.error('[PostHog] getFeatureFlag failed', e);
    return undefined;
  }
};

export const posthogIsFeatureFlagEnabled = async (key: string): Promise<boolean> => {
  try {
    if (!posthog) return false;
    const cached = posthog.isFeatureEnabled(key);
    if (cached !== undefined) return cached;
    await ensureFlagsLoaded();
    return posthog.isFeatureEnabled(key) ?? false;
  } catch (e) {
    console.error('[PostHog] isFeatureFlagEnabled failed', e);
    return false;
  }
};
